import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  schemaJson: Record<string, unknown>;
  ogImage?: string;
  ogImageAlt?: string;
  siteName?: string;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath,
  schemaJson,
  ogImage,
  ogImageAlt,
  siteName,
}: SEOHeadProps) {
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || "https://example.com";
  const canonicalUrl = `${siteUrl}${canonicalPath}`;
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = `ld-json-${canonicalPath.replace(/\//g, "-") || "home"}`;
    script.text = JSON.stringify(schemaJson);

    const existing = document.getElementById(script.id);
    if (existing) existing.remove();
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(script.id);
      if (el) el.remove();
    };
  }, [schemaJson, canonicalPath]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content={today} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content="en_AU" />
      {siteName && <meta property="og:site_name" content={siteName} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && (
        <meta property="og:image:alt" content={ogImageAlt || title} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {ogImage && (
        <meta name="twitter:image:alt" content={ogImageAlt || title} />
      )}
    </>
  );
}
